import React, { useEffect, useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, Tooltip, Popup } from "react-leaflet";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { fmtDate, fmtBattery } from "../../utils/format";
import { getUserCompanyId } from "../../utils/user";
import { iconGreen, iconBlue } from "../../utils/mapIcons";
import { AutoFitBounds } from "../../utils/map";

// helpers
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function DashboardPrincipalSupervisor() {
  const [company, setCompany] = useState({
    id: null,
    name: "—",
    resolving: true,
    error: null,
  });
  const [padlocks, setPadlocks] = useState([]); // /padlock (todos)
  const [latest, setLatest] = useState([]);     // /padlock/latest-locations (todos)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // 1) Resolver empresa del usuario (local → fallback GET /users/:id)
  useEffect(() => {
    let alive = true;
    async function resolveCompany() {
      try {
        const raw = localStorage.getItem("user");
        const u = raw ? JSON.parse(raw) : null;

        const localCompanyId = getUserCompanyId(u);
        const localCompanyName =
          (u && (u.company?.name ?? u.companyName ?? u.company_name)) || null;

        if (localCompanyId !== null) {
          if (alive)
            setCompany({
              id: localCompanyId,
              name: localCompanyName || "—",
              resolving: false,
              error: null,
            });
          return;
        }

        const uid = toNum(u?.id ?? u?.userId ?? u?.user_id);
        if (uid === null) {
          if (alive)
            setCompany({
              id: null,
              name: "—",
              resolving: false,
              error: "Usuario inválido",
            });
          return;
        }

        const r = await api.get(endpoints.users.byId(uid));
        const data = r.data?.data ?? r.data ?? {};
        const apiCompanyId = getUserCompanyId(data);
        const apiCompanyName =
          data?.company?.name ?? data?.companyName ?? data?.company_name ?? null;

        if (alive) {
          setCompany({
            id: apiCompanyId,
            name: apiCompanyName || "—",
            resolving: false,
            error: null,
          });
        }
      } catch (e) {
        if (alive)
          setCompany({
            id: null,
            name: "—",
            resolving: false,
            error: String(e),
          });
      }
    }
    resolveCompany();
    return () => {
      alive = false;
    };
  }, []);

  // 2) Cargar /padlock y /padlock/latest-locations (una vez)
  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setErr(null);
      try {
        const [listRes, latestRes] = await Promise.all([
          api.get(endpoints.padlock.list),
          api.get(endpoints.padlock.latestLocations),
        ]);

        const allPadlocks = Array.isArray(listRes.data)
          ? listRes.data
          : listRes.data?.data ?? [];
        const allLatest = Array.isArray(latestRes.data)
          ? latestRes.data
          : latestRes.data?.data ?? [];

        if (!alive) return;
        setPadlocks(allPadlocks);
        setLatest(allLatest);
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  // 3) Filtrar candados por empresa
  const companyPadlocks = useMemo(() => {
    if (company.resolving) return [];
    if (company.id === null) return [];
    return padlocks.filter((p) => {
      const pid = toNum(p?.company?.id ?? p?.companyId ?? p?.company_id);
      return pid === company.id;
    });
  }, [padlocks, company]);

  // 4) Base por ID para merge
  const baseById = useMemo(() => {
    const map = new Map();
    for (const p of companyPadlocks) {
      const id = toNum(p?.id);
      if (id === null) continue;
      map.set(id, {
        id,
        name: p?.name ?? "—",
        serialNumber: p?.serialNumber ?? "—",
        status: (p?.status ?? "UNKNOWN").toUpperCase(),
      });
    }
    return map;
  }, [companyPadlocks]);

  // 5) latest-locations sólo de esta empresa
  const latestCompany = useMemo(() => {
    return latest.filter((it) => {
      const pid = toNum(it?.padlockId ?? it?.id ?? it?.padlock?.id ?? it?.padlock_id);
      return pid !== null && baseById.has(pid);
    });
  }, [latest, baseById]);

  // 6) Markers (NORMALIZA batería en % o V)
  const markers = useMemo(() => {
    return latestCompany
      .map((it) => {
        const pid = toNum(it?.padlockId ?? it?.id ?? it?.padlock?.id ?? it?.padlock_id);
        if (pid === null) return null;
        const base = baseById.get(pid);
        if (!base) return null;

        const lat = toNum(it?.lat ?? it?.latitude ?? null);
        const lng = toNum(it?.lng ?? it?.longitude ?? null);
        if (lat === null || lng === null) return null;

        const moving = base.status === "MOVING";

        // 🔋 normalización de batería
        const pct = toNum(
          it?.lastTelemetry?.batteryPct ??
          it?.batteryPct ??
          it?.battery_percentage ??
          it?.battery_pct ??
          it?.battery
        );
        const battV = toNum(
          it?.lastTelemetry?.batteryV ??
          it?.batteryV ??
          it?.battery_volts ??
          it?.batteryVoltage
        );

        return {
          id: pid,
          name: base.name,
          serialNumber: base.serialNumber,
          status: base.status,
          moving,
          batteryPct: pct,
          batteryV: battV,
          lastSyncAt: it?.lastTelemetry?.lastSyncAt ?? it?.lastSyncAt ?? null,
          lat,
          lng,
          eventAt: it?.updated ?? it?.eventAt ?? it?.lastLocation?.eventAt ?? null,
          location: it?.location ?? null,
        };
      })
      .filter(Boolean);
  }, [latestCompany, baseById]);

  // 7) Métricas
  const totalDevices = companyPadlocks.length;
  const totalActive = useMemo(
    () =>
      companyPadlocks.filter(
        (d) => (d?.status ?? "UNKNOWN").toUpperCase() === "ACTIVE"
      ).length,
    [companyPadlocks]
  );
  const totalInMotion = useMemo(
    () => markers.filter((m) => m.moving).length,
    [markers]
  );
  const lastUpdate = useMemo(() => {
    const times = markers
      .map((m) => m.eventAt || m.lastSyncAt)
      .filter(Boolean)
      .map((t) => +new Date(t));
    if (!times.length) return null;
    return new Date(Math.max(...times));
  }, [markers]);

  const companyName = company.name;

  return (
    <div className="space-y-6 p-4">
      <h1 className="text-2xl font-semibold">Panel de control</h1>
      <p className="text-gray-600">
        Resumen de los dispositivos de{" "}
        <span className="font-medium">{companyName}</span>
      </p>

      {!company.resolving && company.id === null && (
        <div className="p-3 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">
          Tu usuario no tiene empresa asignada. No se muestran dispositivos.
        </div>
      )}

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Dispositivos activos</p>
          <h2 className="text-3xl font-bold text-green-600">{totalActive}</h2>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">En movimiento</p>
          <h2 className="text-3xl font-bold text-blue-600">{totalInMotion}</h2>
          <p className="text-gray-400 text-xs">Se añadirá en futuras versiones</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Última actualización</p>
          <h2 className="text-lg font-semibold">
            {lastUpdate ? fmtDate(lastUpdate) : "—"}
          </h2>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Total candados</p>
          <h2 className="text-3xl font-bold text-gray-800">{totalDevices}</h2>
        </div>
      </div>

      {/* Mapa */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Últimas localizaciones</h2>
        <div className="relative h-[500px] rounded-lg overflow-hidden">
          <MapContainer className="absolute inset-0 z-0" center={[40.4168, -3.7038]} zoom={5}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            {markers.length > 0 && (
              <AutoFitBounds
                points={markers.map(({ lat, lng }) => ({ lat, lng }))}
              />
            )}

            {markers.map((m) => (
              <Marker
                key={m.id ?? m.serialNumber}
                position={[m.lat, m.lng]}
                icon={m.moving ? iconGreen : iconBlue}
              >
                {/* Tooltip (hover): solo serial y estado con emoticono */}
                <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                  <div className="text-sm">
                    <div className="font-medium">{m.serialNumber}</div>
                    <div className="text-xs flex items-center gap-1">
                      {m.moving ? (
                        <>
                          <span role="img" aria-label="en movimiento">
                            🟩
                          </span>
                          <span>En movimiento</span>
                        </>
                      ) : (
                        <>
                          <span role="img" aria-label="parado">
                            🟥
                          </span>
                          <span>Parado</span>
                        </>
                      )}
                    </div>
                  </div>
                </Tooltip>

                {/* Popup (click): detalles */}
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold mb-1">
                      {m.name}{" "}
                      <span className="text-gray-500">({m.serialNumber})</span>
                    </div>
                    <div>
                      <span className="font-medium flex items-center gap-1">
                        {m.moving ? (
                          <>
                            <span role="img" aria-label="en movimiento">
                              🟩 En movimiento
                            </span>
                            
                          </>
                        ) : (
                          <>
                            <span role="img" aria-label="parado">
                              🟥 Parado
                            </span>
                            
                          </>
                        )}
                      </span>
                    </div>
                    <div>
                      Coordenadas:{" "}
                      <span className="font-medium">
                        {m.lat.toFixed(6)}, {m.lng.toFixed(6)}
                      </span>
                    </div>
                    <div>
                      Último evento:{" "}
                      <span className="font-medium">{fmtDate(m.eventAt)}</span>
                    </div>
                    <div>
                      Batería:{" "}
                      <span className="font-medium">
                        {m?.batteryPct != null ? `${m.batteryPct}%` : "—"}
                      </span>
                    </div>
                    <div>
                      Última sync:{" "}
                      <span className="font-medium">{fmtDate(m.lastSyncAt)}</span>
                    </div>
                    <div>
                      Ubicación:{" "}
                      <span className="font-medium">{m.location ?? "—"}</span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {loading && <div className="mt-3 text-gray-500">Cargando…</div>}
        {err && (
          <div className="mt-3 text-red-600">
            <p className="font-semibold">Error al cargar datos</p>
            <pre className="whitespace-pre-wrap text-xs">{String(err)}</pre>
          </div>
        )}
        {!loading && !err && markers.length === 0 && company.id !== null && (
          <div className="mt-3 text-gray-500">Sin posiciones recientes.</div>
        )}
      </div>
    </div>
  );
}